import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Landmark } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "intestatario", l: "Intestatario" },
  { k: "iban", l: "IBAN" },
  { k: "banca", l: "Banca (opzionale)" },
  { k: "causale", l: "Causale", ph: "Canone Incassi Cloud - {agenzia}" },
];

export const BonificoSettingsButton = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) api.get("/master/settings/bonifico").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [open]);
  const save = async () => {
    try {
      await api.put("/master/settings/bonifico", form);
      toast.success("Coordinate bonifico salvate");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" data-testid="master-bonifico-settings">
        <Landmark className="w-3.5 h-3.5 mr-2" /> Coordinate bonifico
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Coordinate per il pagamento con bonifico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {FIELDS.map((f) => (
              <div key={f.k}>
                <Label>{f.l}</Label>
                <Input value={form[f.k] || ""} placeholder={f.ph || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`bonifico-${f.k}`} />
              </div>
            ))}
            <div className="text-xs text-slate-500">Usate nelle email di promemoria e nella pagina Abbonamento delle agenzie con piano personalizzato. Nella causale puoi usare <code>{"{agenzia}"}</code>.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={save} data-testid="bonifico-settings-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const bonificoBadge = (a) => {
  const bs = a.billing_status || {};
  if (bs.blocked) return { label: "Bloccata (bonifico)", cls: "bg-red-500/20 text-red-300 border-red-700" };
  if (bs.bonifico_overdue) return { label: "Scaduto · in tolleranza", cls: "bg-amber-500/20 text-amber-300 border-amber-700" };
  if (a.bonifico_pagato) return { label: "Pagato", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-700" };
  return { label: "Non pagato", cls: "bg-slate-500/20 text-slate-300 border-slate-700" };
};

export const BonificoStatusPanel = ({ agency, onUpdated }) => {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const b = bonificoBadge(agency);
  const send = async (pagato) => {
    setBusy(true);
    try {
      const r = await api.post(`/master/agencies/${agency.id}/bonifico`, { pagato, prossima_scadenza: date || undefined });
      toast.success(pagato ? "Pagamento registrato" : "Segnato come non pagato");
      setDate("");
      onUpdated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="col-span-2 rounded-lg border border-slate-200 p-3 space-y-3" data-testid="bonifico-status-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pagamento canone (bonifico)</div>
          <div className="mt-1">Scadenza attuale: <strong data-testid="bonifico-current-due">{agency.current_period_end ? new Date(agency.current_period_end).toLocaleDateString("it-IT") : "—"}</strong></div>
        </div>
        <Badge variant="outline" className={b.cls} data-testid="bonifico-status-badge">{b.label}</Badge>
      </div>
      <div>
        <Label>Nuova scadenza (opzionale)</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bonifico-new-due" />
        <div className="text-[11px] text-slate-500 mt-1">Se lasci vuoto, &quot;Segna come pagato&quot; sposta la scadenza di un periodo (mese/anno).</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => send(true)} data-testid="bonifico-mark-paid">Segna come pagato</Button>
        <Button size="sm" disabled={busy} variant="outline" onClick={() => send(false)} data-testid="bonifico-mark-unpaid">Segna come non pagato</Button>
      </div>
    </div>
  );
};
